import { Code, DocumentText, PencilSquare, Plus, Trash, TrianglesMini } from '@medusajs/icons';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  FocusModal,
  IconButton,
  Input,
  Prompt,
  Select,
  Skeleton,
  StatusBadge,
  Table,
  Text,
  toast,
} from '@pcrm/fe/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Field } from '../../components/field';
import { PaperPreview } from '../../components/paper-preview';
import { SectionContainer, SectionEmpty, SectionHeader } from '../../components/section';
import { ApiError } from '../../lib/api';
import { formatDate, formatMoney } from '../../lib/format';
import { useHasPermission } from '../auth/use-session';
import type { Customer } from '../customers/customers.api';
import {
  BILLING_QUERY_KEY,
  billingMonthLabel,
  billingMonthsLabel,
  currentBillingMonth,
  deleteInvoices,
  invoiceFileUrl,
  shiftMonth,
} from './billing.api';
import { FileLink, rateUpdateNotice } from './billing-screen';
import {
  type CustomerBillingView,
  type CustomerInvoice,
  customerBillingQueryOptions,
  type InvoiceFormView,
  issueManualInvoice,
  LINE_CURRENCIES,
  type LineCurrency,
  loadInvoiceForm,
  type ManualInvoicePreview,
  type ManualInvoiceRequest,
  type MonthlyPrefill,
  prefillMonthlyInvoice,
  previewManualInvoice,
  updateManualInvoice,
} from './customer-billing.api';
import {
  emptyLine,
  type FormErrors,
  fromPrefill,
  INVOICE_KIND_LABELS,
  type InvoiceFormDraft,
  type InvoiceKind,
  LINE_DESCRIPTION_MAX,
  type LineDraft,
  toggleMonth,
  toRequest,
} from './manual-invoice-form';

/** Câte luni în urmă oferă selectorul facturii de abonament. */
const MONTHS_BACK = 24;

/**
 * Câte luni înainte (fondatorul, 2026-10-13, #210): clientul care plătește un an în avans își
 * alege anul întreg.
 */
const MONTHS_AHEAD = 12;

/**
 * Fișa clientului → Financiar ([#186](https://github.com/bplugaru/efx-pcrm/issues/186)): facturile
 * clientului, cu PDF și XML, și „Emite factură" — o singură factură, emisă pe loc. O factură care
 * n-a plecat la ANAF se corectează și se șterge ([#201](https://github.com/bplugaru/efx-pcrm/issues/201)).
 *
 * Butonul e dezactivat **cu motivul scris** când clientul nu poate fi facturat: aceleași motive ca
 * în Conta → Facturare, din același loc.
 */
export function CustomerBilling({ customer }: { customer: Customer | null }) {
  const can = useHasPermission();

  if (customer === null) {
    return (
      <SectionContainer>
        <SectionHeader title="Financiar" />
        <SectionEmpty>Salvează întâi fișa clientului.</SectionEmpty>
      </SectionContainer>
    );
  }

  if (!can('invoices.view')) {
    return (
      <SectionContainer>
        <SectionHeader title="Financiar" />
        <SectionEmpty>Nu ai acces la facturi.</SectionEmpty>
      </SectionContainer>
    );
  }

  return <CustomerInvoices customer={customer} canIssue={can('invoices.issue')} />;
}

function CustomerInvoices({ customer, canIssue }: { customer: Customer; canIssue: boolean }) {
  const queryClient = useQueryClient();
  const { data, isPending, error } = useQuery(customerBillingQueryOptions(customer.id));
  const [editing, setEditing] = useState<InvoiceTarget | null>(null);
  const [deleting, setDeleting] = useState<CustomerInvoice | null>(null);

  const remove = useMutation({
    mutationFn: (invoice: CustomerInvoice) => deleteInvoices([invoice.id]),
    onSuccess: async () => {
      toast.success(`Factura ${deleting?.series ?? ''} ${deleting?.number ?? ''} a fost ștearsă.`);
      setDeleting(null);
      // `['billing']` cuprinde și lista din Conta → Facturare: rândul lunii revine la „Neemisă".
      await queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
    },
    onError: (failure: unknown) => {
      toast.error(failure instanceof ApiError ? failure.message : 'Factura nu a putut fi ștearsă.');
    },
  });

  const blocked = (data?.blockers.length ?? 0) > 0;

  return (
    <SectionContainer>
      <SectionHeader
        title="Financiar"
        subtitle="Facturile clientului."
        actions={
          canIssue ? (
            <Button
              size="small"
              disabled={data === undefined || blocked}
              onClick={() => setEditing({ kind: 'new' })}
            >
              <Plus />
              Emite factură
            </Button>
          ) : null
        }
      />

      {canIssue && data && blocked ? (
        <div className="flex flex-col gap-y-1 px-6 py-3" role="note">
          <span className="flex items-center gap-x-2">
            <StatusBadge color="red">Nu se poate factura</StatusBadge>
          </span>
          {data.blockers.map((reason) => (
            <Text key={reason.code + reason.message} size="small" className="text-ui-fg-subtle">
              {reason.message}
            </Text>
          ))}
        </div>
      ) : null}

      {isPending ? (
        <div className="flex flex-col gap-3 px-6 py-4">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-2/3" />
        </div>
      ) : error ? (
        <SectionEmpty tone="error">
          {error instanceof Error ? error.message : 'Facturile nu au putut fi citite.'}
        </SectionEmpty>
      ) : data.invoices.length === 0 ? (
        <SectionEmpty>Nicio factură.</SectionEmpty>
      ) : (
        <InvoiceTable
          invoices={data.invoices}
          canIssue={canIssue}
          onEdit={(invoice) => setEditing({ kind: 'edit', invoice })}
          onDelete={(invoice) => setDeleting(invoice)}
        />
      )}

      {data ? (
        <InvoiceModal
          target={editing}
          customer={customer}
          view={data}
          onClose={() => setEditing(null)}
        />
      ) : null}

      <Prompt
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        variant="danger"
      >
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>
              Ștergi factura {deleting?.series} {deleting?.number}?
            </Prompt.Title>
            <Prompt.Description>
              Dacă e ultima factură din serie, numărul ei se refolosește la următoarea. Dacă după ea
              mai sunt facturi, numărul rămâne gol în serie.
            </Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Renunță</Prompt.Cancel>
            <Prompt.Action
              onClick={(event) => {
                event.preventDefault();
                if (deleting) {
                  remove.mutate(deleting);
                }
              }}
            >
              Șterge
            </Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </SectionContainer>
  );
}

/** Ce deschide formularul: o factură nouă, sau corectura uneia emise. */
type InvoiceTarget =
  | { readonly kind: 'new' }
  | { readonly kind: 'edit'; readonly invoice: CustomerInvoice };

/**
 * Lista: seria și numărul, data, luna facturată (dacă are), totalul, PDF-ul, XML-ul și, cât
 * factura n-a plecat la ANAF, corectura și ștergerea. PDF-ul din fișa clientului e criteriul de
 * acceptare al epicului (#170).
 */
function InvoiceTable({
  invoices,
  canIssue,
  onEdit,
  onDelete,
}: {
  invoices: readonly CustomerInvoice[];
  canIssue: boolean;
  onEdit: (invoice: CustomerInvoice) => void;
  onDelete: (invoice: CustomerInvoice) => void;
}) {
  // Firma apare doar dacă facturile vin din mai multe: aceeași serie poate fi la două firme.
  const manyCompanies = new Set(invoices.map((invoice) => invoice.companyId)).size > 1;

  return (
    <Table>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Factura</Table.HeaderCell>
          <Table.HeaderCell>Data</Table.HeaderCell>
          <Table.HeaderCell>Luna facturată</Table.HeaderCell>
          <Table.HeaderCell className="w-[120px] text-right">Total</Table.HeaderCell>
          <Table.HeaderCell className="w-[240px]" />
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {invoices.map((invoice) => (
          <Table.Row key={invoice.id}>
            <Table.Cell>
              <div className="flex flex-col gap-y-1">
                <span className="flex flex-wrap items-center gap-x-2">
                  <span className="font-medium">
                    {invoice.series} {invoice.number}
                  </span>
                  {invoice.editable ? null : (
                    <Badge size="2xsmall" color="green">
                      La ANAF
                    </Badge>
                  )}
                </span>
                {manyCompanies ? (
                  <Text size="xsmall" className="text-ui-fg-muted">
                    {invoice.companyName}
                  </Text>
                ) : null}
                {invoice.files.error ? (
                  <Text size="xsmall" className="text-ui-fg-error">
                    Fișierele se refac: {invoice.files.error}
                  </Text>
                ) : null}
              </div>
            </Table.Cell>
            <Table.Cell>{formatDate(invoice.issueDate)}</Table.Cell>
            <Table.Cell className="text-ui-fg-subtle">
              {invoice.months.length === 0
                ? INVOICE_KIND_LABELS.free.toLowerCase()
                : billingMonthsLabel(invoice.months)}
            </Table.Cell>
            <Table.Cell className="text-right">{formatMoney(invoice.totals.payable)}</Table.Cell>
            <Table.Cell>
              <div className="flex items-center gap-x-1">
                <FileLink
                  href={invoiceFileUrl(invoice.id, 'pdf')}
                  ready={invoice.files.pdf}
                  label="PDF"
                >
                  <DocumentText />
                </FileLink>
                <FileLink
                  href={invoiceFileUrl(invoice.id, 'xml')}
                  ready={invoice.files.xml}
                  label="XML"
                >
                  <Code />
                </FileLink>
                {canIssue && invoice.editable ? (
                  <>
                    <IconButton
                      size="small"
                      variant="transparent"
                      aria-label={`Corectează factura ${invoice.series} ${invoice.number}`}
                      title="Corectează"
                      onClick={() => onEdit(invoice)}
                    >
                      <PencilSquare />
                    </IconButton>
                    <IconButton
                      size="small"
                      variant="transparent"
                      aria-label={`Șterge factura ${invoice.series} ${invoice.number}`}
                      title="Șterge"
                      onClick={() => onDelete(invoice)}
                    >
                      <Trash />
                    </IconButton>
                  </>
                ) : null}
              </div>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
}

function initialForm(issueDate: string): InvoiceFormDraft {
  return { kind: 'monthly', months: [currentBillingMonth()], issueDate, dueDate: '', lines: [] };
}

/** Factura înghețată → formularul corecturii. */
function fromInvoice(view: InvoiceFormView): InvoiceFormDraft {
  return {
    kind: view.kind,
    months: view.months.length > 0 ? view.months : [currentBillingMonth()],
    issueDate: view.issueDate,
    dueDate: view.dueDate,
    lines: fromPrefill(view.lines),
  };
}

/**
 * „Emite factură" și „Corectează": formularul, previzualizarea, confirmarea.
 *
 * ## Ce nu se editează
 *
 * Clientul (datele se iau din fișă, deci o corectură a numelui sau a adresei se face întâi în
 * fișă), seria (a firmei), moneda facturii (lei). **Data facturii se alege** (#201), oricare —
 * și din urmă, înaintea facturilor deja emise (fondatorul a cerut scoasă verificarea ordinii).
 *
 * ## Previzualizarea și confirmarea
 *
 * „Previzualizează" arată factura cu modelul firmei, **cu seria și numărul pe care le va purta**.
 * „Confirmă" trimite același formular, cu amprenta previzualizării; dacă ceva s-a schimbat între
 * timp — inclusiv numărul, luat de altă factură —, serverul nu emite nimic, iar formularul cere o
 * previzualizare nouă.
 */
function InvoiceModal({
  target,
  customer,
  view,
  onClose,
}: {
  target: InvoiceTarget | null;
  customer: Customer;
  view: CustomerBillingView;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<InvoiceFormDraft>(() => initialForm(view.issueDate));
  const [errors, setErrors] = useState<FormErrors>({});
  const [prefill, setPrefill] = useState<MonthlyPrefill | null>(null);
  const [notes, setNotes] = useState<readonly string[]>([]);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{
    readonly request: ManualInvoiceRequest;
    readonly result: ManualInvoicePreview;
  } | null>(null);
  /**
   * Lunile cerute ultima dată, `2026-10,2026-11`: un răspuns pentru o alegere lăsată între timp —
   * o bifă pusă cât încă venea precompletarea — nu suprascrie liniile.
   */
  const requestedMonths = useRef<string | null>(null);
  const vatPayer = view.company.vatPayer;
  const editing = target?.kind === 'edit' ? target.invoice : null;

  const loadPrefill = useMutation({
    mutationFn: (months: readonly string[]) => prefillMonthlyInvoice(customer.id, months),
    onSuccess: (result) => {
      const key = result.months.join(',');

      if (key !== requestedMonths.current) {
        return;
      }

      setPrefill(result);
      setForm((current) =>
        current.kind === 'monthly' && current.months.join(',') === key
          ? { ...current, lines: fromPrefill(result.lines) }
          : current,
      );
    },
    onError: (failure: unknown) => {
      toast.error(
        failure instanceof ApiError ? failure.message : 'Liniile nu au putut fi precompletate.',
      );
    },
  });

  const loadInvoice = useMutation({
    mutationFn: (invoiceId: string) => loadInvoiceForm(customer.id, invoiceId),
    onSuccess: (result) => {
      setForm(fromInvoice(result));
      setNotes(result.notes);
    },
    onError: (failure: unknown) => {
      toast.error(failure instanceof ApiError ? failure.message : 'Factura nu a putut fi citită.');
      onClose();
    },
  });

  const choosePrefill = (months: readonly string[]) => {
    setPrefill(null);

    if (months.length === 0) {
      // Nicio lună bifată: nimic de precompletat, iar „Previzualizează" spune ce lipsește.
      requestedMonths.current = null;
      return;
    }

    requestedMonths.current = months.join(',');
    loadPrefill.mutate(months);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: formularul pornește de la zero la fiecare deschidere.
  useEffect(() => {
    if (target === null) {
      return;
    }

    const start = initialForm(view.issueDate);
    setForm(target.kind === 'edit' ? { ...start, lines: [] } : start);
    setErrors({});
    setNotes([]);
    setPreview(null);
    setPreviewError(null);
    requestedMonths.current = null;
    setPrefill(null);

    if (target.kind === 'edit') {
      loadInvoice.mutate(target.invoice.id);
    } else {
      choosePrefill(start.months);
    }
  }, [target]);

  const setKind = (kind: InvoiceKind) => {
    setErrors({});
    setPreviewError(null);

    // La corectură, liniile scrise rămân: tipul schimbă doar dacă factura are lună.
    if (editing) {
      setForm((current) => ({ ...current, kind }));
      return;
    }

    if (kind === 'free') {
      // Echipamente și servicii: fără lună și fără linii precompletate.
      requestedMonths.current = null;
      setPrefill(null);
      setForm((current) => ({ ...current, kind, lines: [emptyLine()] }));
      return;
    }

    setForm((current) => ({ ...current, kind, lines: [] }));
    choosePrefill(form.months);
  };

  /**
   * O lună bifată sau debifată. La factura nouă, liniile se precompletează din nou, pe toate lunile
   * bifate; la corectură, liniile scrise rămân — se schimbă doar lunile pe care le ocupă factura.
   */
  const setMonths = (months: readonly string[]) => {
    setErrors({});
    setPreviewError(null);

    if (editing) {
      setForm((current) => ({ ...current, months }));
      return;
    }

    setForm((current) => ({ ...current, months, lines: [] }));
    choosePrefill(months);
  };

  const setField = (field: 'issueDate' | 'dueDate', value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const setLine = (index: number, patch: Partial<LineDraft>) => {
    setForm((current) => ({
      ...current,
      lines: current.lines.map((line, position) =>
        position === index ? { ...line, ...patch } : line,
      ),
    }));
    setErrors((current) => {
      const next = { ...current };
      for (const field of Object.keys(patch)) {
        delete next[`lines.${index}.${field}`];
      }
      return next;
    });
  };

  const removeLine = (index: number) => {
    setErrors({});
    setForm((current) => ({
      ...current,
      lines: current.lines.filter((_, position) => position !== index),
    }));
  };

  const addLine = () =>
    setForm((current) => ({ ...current, lines: [...current.lines, emptyLine()] }));

  const runPreview = useMutation({
    mutationFn: (request: ManualInvoiceRequest) =>
      previewManualInvoice(customer.id, request, editing?.id),
    onSuccess: (result, request) => {
      setPreviewError(null);
      setPreview({ request, result });
    },
    onError: (failure: unknown) => {
      setPreviewError(
        failure instanceof ApiError ? failure.message : 'Previzualizarea nu a putut fi făcută.',
      );
    },
  });

  const confirm = useMutation({
    mutationFn: (input: { request: ManualInvoiceRequest; fingerprint: string }) => {
      const body = { ...input.request, fingerprint: input.fingerprint };

      return editing
        ? updateManualInvoice(customer.id, editing.id, body)
        : issueManualInvoice(customer.id, body);
    },
    onSuccess: async (invoice) => {
      toast.success(
        editing
          ? `Factura ${invoice.series} ${invoice.number} a fost corectată. PDF-ul și XML-ul se refac.`
          : `Factura ${invoice.series} ${invoice.number} a fost emisă.`,
      );
      onClose();
      // `['billing']` cuprinde fișa și lista din Conta → Facturare: rândul lunii devine „Emisă".
      await queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
    },
    onError: async (failure: unknown) => {
      // Ce a confirmat omul nu mai e ce ar ieși, sau nu mai iese deloc: înapoi la formular, cu
      // motivul scris. Nu se salvează nimic pe o factură pe care n-a văzut-o nimeni.
      setPreview(null);
      setPreviewError(
        failure instanceof ApiError ? failure.message : 'Factura nu a putut fi salvată.',
      );
      await queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
    },
  });

  const submitPreview = () => {
    const outcome = toRequest(form, { vatPayer });

    if ('errors' in outcome) {
      setErrors(outcome.errors);
      return;
    }

    setErrors({});
    runPreview.mutate(outcome.request);
  };

  const monthBlocked = form.kind === 'monthly' && (prefill?.blockers.length ?? 0) > 0;
  const loadingLines =
    (form.kind === 'monthly' && form.months.length > 0 && loadPrefill.isPending) ||
    loadInvoice.isPending;
  const title = preview
    ? `Factura ${preview.result.series} ${preview.result.number}`
    : editing
      ? `Corectează factura ${editing.series} ${editing.number}`
      : 'Factură nouă';

  return (
    <FocusModal open={target !== null} onOpenChange={(next) => !next && onClose()}>
      <FocusModal.Content>
        <FocusModal.Header>
          <div className="flex w-full items-center justify-between gap-x-2">
            <FocusModal.Title asChild>
              <Text size="large" weight="plus">
                {title} — {customer.nickname}
              </Text>
            </FocusModal.Title>
            <FocusModal.Description className="sr-only">
              Factura clientului, cu numărul din seria firmei.
            </FocusModal.Description>
            <div className="flex items-center gap-x-2">
              {preview ? (
                <>
                  <Button
                    size="small"
                    variant="secondary"
                    disabled={confirm.isPending}
                    onClick={() => setPreview(null)}
                  >
                    Înapoi la formular
                  </Button>
                  <Button
                    size="small"
                    disabled={confirm.isPending}
                    onClick={() =>
                      confirm.mutate({
                        request: preview.request,
                        fingerprint: preview.result.fingerprint,
                      })
                    }
                  >
                    {editing ? 'Salvează corectura' : 'Confirmă emiterea'}
                  </Button>
                </>
              ) : (
                <Button
                  size="small"
                  disabled={runPreview.isPending || loadingLines || (!editing && monthBlocked)}
                  onClick={submitPreview}
                >
                  Previzualizează
                </Button>
              )}
            </div>
          </div>
        </FocusModal.Header>
        <FocusModal.Body className="overflow-auto bg-ui-bg-subtle p-6">
          {preview ? (
            <div className="mx-auto flex max-w-4xl flex-col gap-y-4">
              <PaperPreview html={preview.result.html} />
            </div>
          ) : (
            <div className="mx-auto flex max-w-5xl flex-col gap-y-6">
              <InvoiceHeader customer={customer} view={view} editing={editing} />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                <Field label="Tip factură" id="factura-tip">
                  <Select
                    value={form.kind}
                    onValueChange={(value) => setKind(value as InvoiceKind)}
                  >
                    <Select.Trigger id="factura-tip">
                      <Select.Value />
                    </Select.Trigger>
                    <Select.Content className="z-50">
                      <Select.Item value="monthly">{INVOICE_KIND_LABELS.monthly}</Select.Item>
                      <Select.Item value="free">{INVOICE_KIND_LABELS.free}</Select.Item>
                    </Select.Content>
                  </Select>
                </Field>

                {form.kind === 'monthly' ? (
                  <Field label="Luna facturată" id="factura-luna" error={errors.months}>
                    <MonthsSelect months={form.months} onChange={setMonths} />
                  </Field>
                ) : (
                  <div className="hidden sm:block" />
                )}

                <Field label="Data facturii" id="factura-data" error={errors.issueDate}>
                  <Input
                    id="factura-data"
                    type="date"
                    value={form.issueDate}
                    onChange={(event) => setField('issueDate', event.target.value)}
                  />
                </Field>

                <Field
                  label="Data scadenței"
                  id="factura-scadenta"
                  hint="Opțională. Implicit, data facturii plus 15 zile."
                >
                  <Input
                    id="factura-scadenta"
                    type="date"
                    min={form.issueDate || undefined}
                    value={form.dueDate}
                    onChange={(event) => setField('dueDate', event.target.value)}
                  />
                </Field>
              </div>

              {form.kind === 'monthly' && !editing ? (
                <PrefillNotes prefill={prefill} months={form.months} loading={loadingLines} />
              ) : null}

              {notes.map((note) => (
                <Text key={note} size="small" className="text-ui-fg-subtle">
                  ⚠ {note}
                </Text>
              ))}

              <LinesEditor
                lines={form.lines}
                errors={errors}
                units={view.units}
                vatPayer={vatPayer}
                onChange={setLine}
                onRemove={removeLine}
                onAdd={addLine}
              />

              <RateNotes lines={form.lines} view={view} />

              {previewError ? (
                <Text size="small" className="text-ui-fg-error" role="alert">
                  {previewError}
                </Text>
              ) : null}
            </div>
          )}
        </FocusModal.Body>
      </FocusModal.Content>
    </FocusModal>
  );
}

/** Clientul și firma care emite, cu seria și numărul pe care le va purta factura. */
function InvoiceHeader({
  customer,
  view,
  editing,
}: {
  customer: Customer;
  view: CustomerBillingView;
  editing: CustomerInvoice | null;
}) {
  const address = [customer.legalAddress, customer.city, customer.county]
    .filter((part): part is string => Boolean(part))
    .join(', ');
  const company = view.company;
  const number = editing
    ? `${editing.series} ${editing.number}`
    : company.series
      ? `${company.series.code} ${company.series.nextNumber}`
      : null;

  return (
    <div className="grid grid-cols-1 gap-4 rounded-lg border border-ui-border-base bg-ui-bg-base p-4 sm:grid-cols-2">
      <div className="flex flex-col gap-y-1">
        <Text size="small" weight="plus">
          Client
        </Text>
        <Text size="small">
          {customer.name} ({customer.nickname})
        </Text>
        {customer.cif ? (
          <Text size="small" className="text-ui-fg-subtle">
            CUI {customer.cif}
          </Text>
        ) : null}
        <Text size="small" className="text-ui-fg-subtle">
          {address || 'Fără adresă'}
        </Text>
      </div>
      <div className="flex flex-col gap-y-1">
        <Text size="small" weight="plus">
          {company.shortName ?? company.name}
        </Text>
        {number ? (
          <Text size="small">
            Factura <span className="font-mono">{number}</span>
          </Text>
        ) : null}
        <Text size="small" className="text-ui-fg-subtle">
          Moneda facturii: lei ·{' '}
          {company.vatPayer
            ? `TVA ${view.vatRate ?? '—'}%`
            : 'firmă neplătitoare de TVA: prețurile sunt finale'}
        </Text>
      </div>
    </div>
  );
}

/**
 * „Luna facturată", cu **alegere multiplă** (#210): fiecare lună se bifează și se debifează, iar
 * meniul rămâne deschis cât se bifează. Lunile nu trebuie să fie legate între ele.
 *
 * Un `DropdownMenu` cu bife, nu un `Select`: `Select` alege una singură. Panoul se portează în
 * formular, nu în `body` — într-un `FocusModal`, `body` e în afara capcanei de focus și meniul s-ar
 * închide în clipa în care se deschide (vezi `DropdownMenuContent`).
 */
function MonthsSelect({
  months,
  onChange,
}: {
  months: readonly string[];
  onChange: (months: readonly string[]) => void;
}) {
  const current = currentBillingMonth();
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const options = useMemo(
    () =>
      Array.from({ length: MONTHS_AHEAD + 1 + MONTHS_BACK }, (_, index) =>
        shiftMonth(current, MONTHS_AHEAD - index),
      ),
    [current],
  );

  return (
    <div ref={setContainer}>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            id="factura-luna"
            type="button"
            // Clasele câmpului `Select` din Medusa, ca în `Combobox`: stă lângă două `Select`-uri.
            className="transition-fg txt-compact-small flex h-8 w-full select-none items-center justify-between gap-x-2 rounded-md bg-ui-bg-field px-2 py-1.5 text-left text-ui-fg-base shadow-buttons-neutral outline-none hover:bg-ui-bg-field-hover focus-visible:shadow-borders-interactive-with-active"
          >
            <span className={months.length === 0 ? 'truncate text-ui-fg-muted' : 'truncate'}>
              {months.length === 0 ? 'Alege lunile' : billingMonthsLabel(months)}
            </span>
            <TrianglesMini className="shrink-0 text-ui-fg-muted" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          container={container ?? undefined}
          className="max-h-80 min-w-56"
        >
          {options.map((option) => (
            <DropdownMenuCheckboxItem
              key={option}
              // Lista pornește cu un an înainte: la deschidere, luna curentă se aduce în vedere.
              ref={option === current ? scrollIntoCenter : undefined}
              checked={months.includes(option)}
              // Meniul rămâne deschis: se bifează mai multe luni la rând.
              onSelect={(event) => event.preventDefault()}
              onCheckedChange={() => onChange(toggleMonth(months, option))}
            >
              {billingMonthLabel(option)}
              {option === current ? ' · luna curentă' : ''}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function scrollIntoCenter(element: HTMLElement | null): void {
  element?.scrollIntoView({ block: 'center' });
}

/** Ce spune precompletarea: de ce luna nu se poate emite, ce n-a intrat, semnele. */
function PrefillNotes({
  prefill,
  months,
  loading,
}: {
  prefill: MonthlyPrefill | null;
  months: readonly string[];
  loading: boolean;
}) {
  if (months.length === 0) {
    return null;
  }

  if (loading || prefill === null) {
    return (
      <Text size="small" className="text-ui-fg-subtle">
        Se precompletează liniile din abonamente…
      </Text>
    );
  }

  return (
    <div className="flex flex-col gap-y-1">
      {prefill.blockers.map((reason) => (
        // Codul se repetă de la o lună la alta (#210): cheia e și mesajul, care numește luna.
        <span key={reason.code + reason.message} className="flex items-start gap-x-2">
          <StatusBadge color="red">Blocat</StatusBadge>
          <Text size="small">{reason.message}</Text>
        </span>
      ))}
      {prefill.blockers.length === 0 && prefill.lines.length === 0 ? (
        <Text size="small" className="text-ui-fg-subtle">
          Clientul n-are abonamente facturabile în {billingMonthsLabel(months)}. Adaugă liniile de
          mână.
        </Text>
      ) : null}
      {[...prefill.reasons, ...prefill.markers].map((note) => (
        <Text key={note.code + note.message} size="small" className="text-ui-fg-subtle">
          ⚠ {note.message}
        </Text>
      ))}
    </div>
  );
}

function LinesEditor({
  lines,
  errors,
  units,
  vatPayer,
  onChange,
  onRemove,
  onAdd,
}: {
  lines: readonly LineDraft[];
  errors: FormErrors;
  units: CustomerBillingView['units'];
  vatPayer: boolean;
  onChange: (index: number, patch: Partial<LineDraft>) => void;
  onRemove: (index: number) => void;
  onAdd: () => void;
}) {
  return (
    <div className="flex flex-col gap-y-3">
      <Text size="small" weight="plus">
        Liniile facturii
      </Text>
      {lines.map((line, index) => {
        const label = `linia ${index + 1}`;
        const error = (field: string) => errors[`lines.${index}.${field}`];

        return (
          <div
            key={line.key}
            className="grid grid-cols-2 items-start gap-2 rounded-lg border border-ui-border-base bg-ui-bg-base p-3 sm:grid-cols-[minmax(0,1fr)_96px_88px_120px_88px_120px_120px_32px]"
          >
            <LineField error={error('description')} className="col-span-2 sm:col-span-1">
              <Input
                aria-label={`Denumire, ${label}`}
                placeholder="Denumire"
                maxLength={LINE_DESCRIPTION_MAX + 20}
                value={line.description}
                onChange={(event) => onChange(index, { description: event.target.value })}
              />
            </LineField>
            <Select
              value={line.unitCode}
              onValueChange={(unitCode) => onChange(index, { unitCode })}
            >
              <Select.Trigger aria-label={`U.M., ${label}`}>
                <Select.Value />
              </Select.Trigger>
              <Select.Content className="z-50">
                {units.map((unit) => (
                  <Select.Item key={unit.code} value={unit.code}>
                    {unit.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
            <LineField error={error('quantity')}>
              <Input
                aria-label={`Cantitate, ${label}`}
                inputMode="decimal"
                value={line.quantity}
                onChange={(event) => onChange(index, { quantity: event.target.value })}
              />
            </LineField>
            <LineField error={error('unitPrice')}>
              <Input
                aria-label={`Preț, ${label}`}
                inputMode="decimal"
                placeholder="0,00"
                value={line.unitPrice}
                onChange={(event) => onChange(index, { unitPrice: event.target.value })}
              />
            </LineField>
            <Select
              value={line.currency}
              onValueChange={(currency) => onChange(index, { currency: currency as LineCurrency })}
            >
              <Select.Trigger aria-label={`Monedă, ${label}`}>
                <Select.Value />
              </Select.Trigger>
              <Select.Content className="z-50">
                {LINE_CURRENCIES.map((currency) => (
                  <Select.Item key={currency} value={currency}>
                    {currency}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
            {/*
              ⚠️ Lângă fiecare preț scrie ce înseamnă. Alegerea e pe linie, nu pe factură: liniile
              precompletate pot veni din abonamente cu convenții diferite. La neplătitoare nu
              apare — prețul e prețul.
            */}
            {vatPayer ? (
              <Select
                value={line.priceIncludesVat ? 'cu' : 'fara'}
                onValueChange={(value) => onChange(index, { priceIncludesVat: value === 'cu' })}
              >
                <Select.Trigger aria-label={`TVA în preț, ${label}`}>
                  <Select.Value />
                </Select.Trigger>
                <Select.Content className="z-50">
                  <Select.Item value="fara">fără TVA</Select.Item>
                  <Select.Item value="cu">cu TVA</Select.Item>
                </Select.Content>
              </Select>
            ) : (
              <Text size="small" className="self-center text-ui-fg-subtle">
                fără TVA
              </Text>
            )}
            {/* Serviciu sau echipament: codul CPV din e-Factura (#211). */}
            <Select
              value={line.isEquipment ? 'echipament' : 'serviciu'}
              onValueChange={(value) => onChange(index, { isEquipment: value === 'echipament' })}
            >
              <Select.Trigger aria-label={`Serviciu sau echipament, ${label}`}>
                <Select.Value />
              </Select.Trigger>
              <Select.Content className="z-50">
                <Select.Item value="serviciu">serviciu</Select.Item>
                <Select.Item value="echipament">echipament</Select.Item>
              </Select.Content>
            </Select>
            <IconButton
              size="small"
              variant="transparent"
              aria-label={`Scoate ${label}`}
              disabled={lines.length <= 1}
              onClick={() => onRemove(index)}
            >
              <Trash />
            </IconButton>
          </div>
        );
      })}
      {errors.lines ? (
        <Text size="small" className="text-ui-fg-error" role="alert">
          {errors.lines}
        </Text>
      ) : null}
      <div>
        <Button size="small" variant="secondary" onClick={onAdd}>
          <Plus />
          Adaugă linie
        </Button>
      </div>
    </div>
  );
}

function LineField({
  error,
  className,
  children,
}: {
  error: string | undefined;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`flex flex-col gap-y-1 ${className ?? ''}`}>
      {children}
      {error ? (
        <Text size="xsmall" className="text-ui-fg-error" role="alert">
          {error}
        </Text>
      ) : null}
    </div>
  );
}

/** Cursul, doar pentru monedele folosite pe linii: cu ce se convertește, sau de ce nu se poate. */
function RateNotes({ lines, view }: { lines: readonly LineDraft[]; view: CustomerBillingView }) {
  const used = new Set<string>(
    lines.map((line) => line.currency).filter((currency) => currency !== 'RON'),
  );
  const rates = view.exchangeRates.filter((rate) => used.has(rate.currency));

  if (rates.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-y-1">
      {rates.map((rate) => (
        <Text
          key={rate.currency}
          size="small"
          className={rate.problem ? 'text-ui-fg-error' : 'text-ui-fg-subtle'}
        >
          {rate.problem ??
            `Curs ${rate.currency}: 1 ${rate.currency} = ${(rate.value ?? '').replace('.', ',')} lei, din ${formatDate(rate.date ?? '')}. Prețul se convertește în lei la emitere, iar cursul rămâne pe factură.`}
        </Text>
      ))}
      {view.exchangeRateUpdateAt !== null ? (
        <Text size="small" className="text-ui-fg-subtle">
          {rateUpdateNotice(view.exchangeRateUpdateAt)}
        </Text>
      ) : null}
    </div>
  );
}
